import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { InfoAndSingleActionComponentVarOne } from 'src/consumer/components/ListingPageComponentsAndHelpers/ListingPageComponents'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { PublicAppFooter } from 'src/consumer/components/PublicAppFooter'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { ClubbedAuthModal } from 'src/consumer/components/SignIn/ClubbedAuthComponents'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import {
  selectCategoriesList,
  setCategoriesList,
} from 'src/consumer/reducers/genericData/genericDataReducer'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { useNavigateWithParams } from 'src/customHooks/customHooks'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  getClinicPublicallyListableEvents,
  getOfflineRegularEvents,
  getOnlineRegularEvents,
} from './utils'

export default function ClinicLandingPage({ showDoctors, showTherapists }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { clinicBrandSlug, clinicId, clinicSlug } = useParams()
  const [clinicBrandData, setClinicBrandData] = useState()
  const [dataLoading, setDataLoading] = useState(false)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('login')
  const [mentorsData, setMentorsData] = useState({})
  const dispatch = useDispatch()

  const categoriesListFromStore = useSelector(selectCategoriesList)

  async function putCategoriesInStoreIfNeeded() {
    if (categoriesListFromStore === null) {
      const response = await getDataWithParams('/api/external/app-listable/list', {
        feature: 'categories',
      })
      if (response?.status === 200 && Array.isArray(response?.data)) {
        dispatch(setCategoriesList(response?.data))
      }
    }
  }

  useEffect(() => {
    putCategoriesInStoreIfNeeded()
  }, [])

  async function pageSetup() {
    setDataLoading(true)
    let clinicBrandData
    if (location?.state?.clinicBrand && location?.state?.clinics) {
      clinicBrandData = { ...location?.state }
      setClinicBrandData({ ...location?.state })
    } else {
      let response = await getDataWithoutParams(
        `/api/external/clinic/brand-and-clinics?slug=${clinicBrandSlug}`,
      )
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        clinicBrandData = response?.data?.data
        setClinicBrandData(response?.data?.data)
      } else {
        navigate('/404')
      }
    }
    const mentors = []
    let thisClinicData
    if (clinicId) {
      thisClinicData = clinicBrandData?.clinics?.find((item) => item?.id === clinicId)
    } else if (clinicSlug) {
      thisClinicData = clinicBrandData?.clinics?.find((item) => item?.slug === clinicSlug)
    }
    const clinicIdParam = thisClinicData?.id
    const clinicSlugParam = thisClinicData?.slug
    if (!clinicIdParam && !clinicSlugParam) {
      setDataLoading(false)
      return
    }
    const landingQuery = clinicIdParam
      ? `clinicId=${encodeURIComponent(clinicIdParam)}`
      : `slug=${encodeURIComponent(clinicSlugParam)}`
    const landingRes = await getPublicDataWithoutParams(
      `/api/public/clinic-landing-page?${landingQuery}`,
    )
    const landingStatus = landingRes?.response?.status ?? landingRes?.status
    const landingBody = landingRes?.response?.data ?? landingRes?.data
    const landingCode = landingBody?.code
    if (landingStatus !== 200 || !isCommonResponseSuccessful(landingCode)) {
      if (landingStatus === 400 && landingCode === 9411) navigate('/404')
      else if (landingStatus === 404 || landingCode === 9414) navigate('/404')
      setDataLoading(false)
      return
    }
    const landingData = landingBody?.data
    const practitioners = Array.isArray(landingData?.practitioners)
      ? landingData.practitioners
      : []
    const nextMentors = {}
    for (const p of practitioners) {
      if (!p?.id) continue
      const profileRes = await getPublicDataWithoutParams(
        `/api/service-provider?id=${encodeURIComponent(p.id)}&slug=${encodeURIComponent(p.slug || '')}`,
      )
      const profileStatus = profileRes?.response?.status ?? profileRes?.status
      const profile = profileStatus === 200 ? profileRes?.data : null
      if (profile && typeof profile === 'object') {
        nextMentors[p.id] = {
          ...profile,
          offlineEvents: p.offlineEvents ?? [],
          onlineEvents: p.onlineEvents ?? [],
        }
      }
    }
    setMentorsData(nextMentors)
    setDataLoading(false)
  }

  useEffect(() => {
    pageSetup()
  }, [])

  let thisClinicData
  if (clinicId) {
    thisClinicData = clinicBrandData?.clinics?.find((item) => item?.id === clinicId)
  } else if (clinicSlug) {
    thisClinicData = clinicBrandData?.clinics?.find((item) => item?.slug === clinicSlug)
  }

  return (
    <>
      {thisClinicData && (
        <SEOComponent
          title={thisClinicData?.name || 'AUMHUM'}
          description={thisClinicData?.description || ''}
          image={thisClinicData?.image && getImageLink(thisClinicData?.image)}
        />
      )}
      {dataLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          <PublicAppHeader
            loginFunction={() => {
              setTypeOfClubbedAuthModal('login')
              setShowClubbedAuthModal(true)
            }}
            registerFunction={() => {
              setTypeOfClubbedAuthModal('signup')
              setShowClubbedAuthModal(true)
            }}
            config={{ showLinks: false, showBackButton: window.history.length > 2 }}
            backButtonFunction={() => window.history.back()}
          />
          <ClubbedAuthModal
            open={showClubbedAuthModal}
            onClose={() => {
              setShowClubbedAuthModal(false)
            }}
            loginVariant={typeOfClubbedAuthModal === 'login'}
          />
          <InfoAndSingleActionComponentVarOne
            headline={thisClinicData?.name}
            description={thisClinicData?.description}
            image={thisClinicData?.image && getImageLink(thisClinicData?.image)}
          />
          {Array.isArray(thisClinicData?.doctors) &&
            thisClinicData?.doctors?.length > 0 &&
            showDoctors && (
              <div style={{ width: '100%', padding: '20px' }}>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <b>
                    <u>Doctors</u>
                  </b>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                  }}
                >
                  {thisClinicData?.doctors?.map((doctor, doctorIndex) => {
                    if (mentorsData.hasOwnProperty(doctor?.uid)) {
                      const thisDoctor = mentorsData[doctor?.uid]
                      return (
                        <MentorComponent
                          metaCategories={categoriesListFromStore}
                          mentorData={thisDoctor}
                          key={doctorIndex}
                          clinicId={thisClinicData?.id}
                          clinicBrandSlug={clinicBrandSlug}
                        />
                      )
                    }
                    return null
                  })}
                </div>
              </div>
            )}
          {Array.isArray(thisClinicData?.therapists) &&
            thisClinicData?.therapists?.length > 0 &&
            showTherapists && (
              <div style={{ width: '100%', padding: '20px' }}>
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <b>
                    <u>Therapists</u>
                  </b>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                  }}
                >
                  {thisClinicData?.therapists
                    .slice()
                    .reverse()
                    ?.map((therapist, therapistIndex) => {
                      if (mentorsData.hasOwnProperty(therapist?.uid)) {
                        const thisTherapist = mentorsData[therapist?.uid]
                        return (
                          <MentorComponent
                            metaCategories={categoriesListFromStore}
                            mentorData={thisTherapist}
                            key={therapistIndex}
                            clinicId={thisClinicData?.id}
                            clinicBrandSlug={clinicBrandSlug}
                          />
                        )
                      }
                      return null
                    })}
                </div>
              </div>
            )}
          <PublicAppFooter />
        </>
      )}
    </>
  )
}

function MentorComponent({ mentorData, clinicBrandSlug, clinicId, metaCategories }) {
  const navigateCustom = useNavigateWithParams()

  const hasApiEvents =
    Array.isArray(mentorData?.offlineEvents) || Array.isArray(mentorData?.onlineEvents)
  const offlineEventTypes = hasApiEvents
    ? (mentorData?.offlineEvents ?? [])
    : getOfflineRegularEvents(
        getClinicPublicallyListableEvents(mentorData?.eventTypes, clinicId),
      )
  const aumhumEventTypes = hasApiEvents
    ? (mentorData?.onlineEvents ?? [])
    : getOnlineRegularEvents(
        getClinicPublicallyListableEvents(mentorData?.eventTypes, clinicId),
      )
  return (
    <div
      style={{
        border: '1px solid #c6c6c6',
        borderRadius: '8px',
        padding: '20px',
        width: '100%',
        maxWidth: '400px',
      }}
    >
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div>
          <img
            src={getImageLink(mentorData?.profilePicId)}
            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
            alt="mentor"
          />
        </div>
        <div>
          <b>{mentorData?.name}</b>
          <div>
            {Array.isArray(mentorData?.categories) &&
              mentorData?.categories
                .map((category) => {
                  const metaCategory = (metaCategories || [])?.find(
                    (item) => item.assessmentId === category,
                  )
                  return metaCategory ? metaCategory.title : ''
                })
                .filter((title) => title)
                .join(', ')}
          </div>
        </div>
      </div>
      <div>{mentorData?.shortDesc || mentorData?.longDesc}</div>
      <div style={{ width: '100%', height: '1px', background: '#c6c6c6', margin: '10px 0' }}></div>
      {offlineEventTypes?.length > 1 || aumhumEventTypes?.length > 1 ? (
        <>
          <div style={{ marginBottom: '10px' }}>
            <u>Choose a session:</u>
          </div>
          <div>In Person:</div>
          <div>
            {offlineEventTypes?.map((item, index) => {
              return (
                <div
                  style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                  key={index}
                  onClick={() => navigateCustom(`/mentor/${mentorData?.slug}/events/${item?.uid}`)}
                >
                  {item?.title}
                </div>
              )
            })}
          </div>
          <div>Online:</div>
          <div>
            {aumhumEventTypes?.map((item, index) => {
              return (
                <div
                  style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                  key={index}
                  onClick={() => navigateCustom(`/mentor/${mentorData?.slug}/events/${item?.uid}`)}
                >
                  {item?.title}
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: '10px' }}>
            {(offlineEventTypes?.length >= 1 || aumhumEventTypes?.length >= 1) && (
              <u>Book a session:</u>
            )}
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            {offlineEventTypes?.length === 1 && (
              <div
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '30px',
                  background: '#705bb9',
                  color: '#fff',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
                onClick={() =>
                  navigateCustom(`/mentor/${mentorData?.slug}/events/${offlineEventTypes[0]?.uid}`)
                }
              >
                Book - In Person Session
              </div>
            )}
            {aumhumEventTypes?.length === 1 && (
              <div
                style={{
                  width: '100%',
                  borderRadius: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '30px',
                  background: '#705bb9',
                  color: '#fff',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
                onClick={() =>
                  navigateCustom(`/mentor/${mentorData?.slug}/events/${aumhumEventTypes[0]?.uid}`)
                }
              >
                Book - Video Session
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
